import React, { JSX } from "react";
import { useAppSelector } from "../../../../../app/hooks";
import { selectUser } from "../../../../../features/user/userSlice";
import { useGetNotificationsQuery } from "../../../../../services/groupNotificationAPI";
import { NotificationState } from "../../../../../features/notifications/notificationSlice";
import { LoadingSpinner, ErrorDisplay } from "../../../../../utils/StateDisplay";
import useGetLanguage from "../../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../../dictionary/ChartsText";

type NotificationSelectProps = {
  groupID: string;
  currentNotificationID?: string | "";
  onNotificationChange: (newNotificationID: string) => void;
};

const NotificationSelectInput = React.memo(
  ({
    groupID,
    currentNotificationID,
    onNotificationChange,
  }: NotificationSelectProps): JSX.Element => {
    const language: LanguageText = useGetLanguage({
      fileName: "ChartsText",
    });
    const userGroupPermissions = useAppSelector(selectUser)?.groups[groupID]?.permissions;

    const {
      data: groupNotificationData,
      isLoading,
      error,
    } = useGetNotificationsQuery(groupID, {
      skip: !(userGroupPermissions?.can_view_notifications && groupID !== ""),
    });

    const handleNotificationChange = ({
      target: { value },
    }: React.ChangeEvent<HTMLSelectElement>) => {
      onNotificationChange(value);
    };

    if (isLoading) {
      return (
        <LoadingSpinner message={language?.charts?.map?.markers?.notification?.loading} />
      );
    }
    if (error) {
      return (
        <ErrorDisplay
          message={language?.charts?.map?.markers?.notification?.errorMessages?.error}
        />
      );
    }

    return (
      <div className="w-full p-4 mb-4 bg-white shadow-lg rounded-lg flex flex-col space-y-2">
        <h4 className="text-sm font-semibold text-center m-0">
          {language?.charts?.map?.markers?.notification?.titles?.notification}
        </h4>
        <div className="flex items-center w-full">
          <select
            id="notification"
            name="notification"
            value={currentNotificationID}
            onChange={handleNotificationChange}
            className="border border-gray-300 rounded p-2 max-h-20 overflow-y-auto box-border flex-grow sm:flex-grow-0 sm:w-full"
          >
            <option value="">
              {language?.charts?.map?.markers?.notification?.select}
            </option>
            {groupNotificationData?.length ? (
              groupNotificationData.map((notification: NotificationState) => (
                <option key={notification.id} value={notification.id}>
                  {notification.name}
                </option>
              ))
            ) : (
              <option value="">
                {language?.charts?.map?.markers?.notification?.notFound}
              </option>
            )}
          </select>
        </div>
      </div>
    );
  }
);

export default NotificationSelectInput;
